import type { Schemas } from '@argzombie/api-client';
import { CORE_STAT_IDS, SKILL_IDS } from '../skills/types';

/*
 * The Effects system content (ADR 0045, card 2; admin card 18): shared-cs Content/StatusEffectDefs.cs and Effects/ModifierRegistry.cs.
 * Leaf types come from the generated client; the entries and blocks are nullable supersets so the validator can report an explicit
 * `null` like the server ("is missing 'modifiers'", "has an empty modifier").
 */

export type ModifierDef = Schemas['ModifierDef'];
export type EffectDurationDef = Schemas['EffectDurationDef'];
export type EffectStackingDef = Omit<Schemas['EffectStackingDef'], 'values'> & {
  values?: number[] | null;
};
export type EffectResistDef = Omit<Schemas['EffectResistDef'], 'ignoreAt'> & {
  ignoreAt?: number[] | null;
};
export type EffectTriggerDef = Omit<Schemas['EffectTriggerDef'], 'params' | 'listParams'> & {
  /** Numbers: each is a minimum on the event's fact. */
  params?: Record<string, number> | null;
  /** Lists (shared-cs `EffectTriggerDef.ListParams`): a `health.workout` trigger's `catIn`, the workout categories that fire it
   *  (Outta-Ten fresh_legs / pumped). */
  listParams?: Record<string, string[]> | null;
};
export type EffectTextDef = Schemas['EffectTextDef'];
export type DerivedEffectDef = Schemas['DerivedEffectDef'];

export type StatusEffectDef = {
  id?: string;
  name?: string;
  family?: string;
  polarity?: string;
  scope?: string;
  appliesTo?: string;
  modifiers?: (ModifierDef | null)[] | null;
  duration?: EffectDurationDef | null;
  stacking?: EffectStackingDef | null;
  resist?: EffectResistDef | null;
  triggers?: (EffectTriggerDef | null)[] | null;
  text?: EffectTextDef | null;
  hidden?: boolean;
  derived?: DerivedEffectDef | null;
  /** shared-cs `StatusEffectDef.Mirror` ('' = none, 'copies' = also on every living QR copy of the player; scope player only). */
  mirror?: string | null;
};

export type PolarZoneDef = Schemas['PolarZoneDef'];
export type GridZoneDef = Schemas['GridZoneDef'];
export type ZoneDef = Omit<Schemas['ZoneDef'], 'effects'> & { effects?: (string | null)[] | null };

export type ModifierClampDef = Schemas['ModifierClampDef'];
export type ZoneDefaultsDef = Schemas['ZoneDefaultsDef'];
export type BaseZoneEffectsDef = {
  home?: (string | null)[] | null;
  camp?: (string | null)[] | null;
};
export type EffectRulesDef = {
  maxActive?: number;
  statCap?: number;
  keys?: Record<string, ModifierClampDef | null> | null;
  /** ADR 0066: family (skill, stat, perk, milestone) -> the hidden derived row its pseudo ids resolve to (`derived.<family>`). */
  derivedFamilies?: Record<string, string> | null;
  zoneDefaults?: ZoneDefaultsDef | null;
  baseZoneEffects?: BaseZoneEffectsDef | null;
};

/** shared-cs `EffectScopes.All`; `team` and `crew` are the group scopes (`appliesTo` only for them). */
export const EFFECT_SCOPES = ['player', 'survivor', 'team', 'crew', 'base'] as const;
export const GROUP_SCOPES = ['team', 'crew'] as const;
/** shared-cs `EffectAppliesTo`. */
export const EFFECT_APPLIES_TO = ['everyone', 'recruits', 'at_base', 'player'] as const;
/** shared-cs `EffectMirrors.All` (Powered Up POC, health intake CONTRACT §11): '' = none. */
export const EFFECT_MIRRORS = ['', 'copies'] as const;
/** shared-cs `EffectPolarities`. */
export const EFFECT_POLARITIES = ['buff', 'debuff'] as const;
/** shared-cs `EffectDurationKinds` (`derived` is computed, never stored). */
export const EFFECT_DURATION_KINDS = ['timed', 'zone', 'until_cleared', 'derived'] as const;
/** shared-cs `EffectStackModes`. */
export const EFFECT_STACK_MODES = ['refresh', 'extend', 'stack', 'ignore'] as const;
/** shared-cs `ZoneShapes` (polar = a circle, grid = a lat/lng box) and `ZoneKinds` (home and camp are the implicit base zones). */
export const ZONE_SHAPES = ['polar', 'grid'] as const;
export const BASE_ZONE_KINDS = ['home', 'camp'] as const;
/** shared-cs `ZoneRules` numbers. */
export const ZONE_MIN_RADIUS_M = 10;
export const ZONE_MAX_RADIUS_M = 5000;
export const ZONE_MAX_GRID_SIDE_M = 5000;

/** shared-cs `StatusEffectTriggers.All` and `ParamsOf`: the server events that can apply an effect. */
export const EFFECT_TRIGGERS = [
  'upkeep.shortage',
  'survivor.lost',
  'raid.haul',
  'recruit.joined',
  'boss.won',
  'holdout.won',
  'lure.extracted',
  'facility.built',
  'beat',
  'health.workout',
  'health.day',
  'health.level',
] as const;
/** Health intake facts (shared-cs `StatusEffectTriggers.Health*Params`). */
const HEALTH_WORKOUT_PARAMS = [
  'tier',
  'cat',
  'effortMin',
  'creditedMin',
  'activeMin',
  'distanceKm',
  'kcal',
  'hrAvg',
  'firstToday',
  'streakDays',
  'dayCreditedMin',
  'weekCreditedMin',
  'band',
  'level',
] as const;
/** The list-valued trigger params (`listParams`; shared-cs `StatusEffectTriggers.ListParamsOf`). */
export const LIST_TRIGGER_PARAMS = ['catIn'] as const;
const TRIGGER_LIST_PARAMS: Record<string, readonly string[]> = {
  'health.workout': LIST_TRIGGER_PARAMS,
};
export const triggerListParams = (event: string | null | undefined): readonly string[] =>
  (event && TRIGGER_LIST_PARAMS[event]) || [];
const TRIGGER_PARAMS: Record<string, readonly string[]> = {
  'raid.haul': ['minHaulKg'],
  'health.workout': HEALTH_WORKOUT_PARAMS,
  'health.day': ['band', 'streakDays', 'dayCreditedMin', 'level'],
  'health.level': ['level', 'weekCreditedMin'],
};
export const triggerParams = (event: string | null | undefined): readonly string[] =>
  (event && TRIGGER_PARAMS[event]) || [];

/** shared-cs `ModifierKey`: a registered key, its op, neutral value, the scopes that may carry it, and who reads it. */
export type ModifierKey = {
  key: string;
  op: 'add' | 'mult';
  neutral: number;
  scopes: readonly string[];
  rideOnTicket: boolean;
  consumer: string;
};

const MEMBER = ['player', 'survivor', 'team', 'crew'] as const;
const BASE = ['base'] as const;
const TEAM = ['team', 'crew', 'player'] as const;
/** The level keys (ADR 0066 §3): `stat.<id>` for the six stats, then `skill.<id>` for every launch skill, each op add. */
const LEVEL_KEYS = [
  ...CORE_STAT_IDS.map((s) => `stat.${s}`),
  ...SKILL_IDS.map((s) => `skill.${s}`),
].map((key): ModifierKey => ({
  key,
  op: 'add',
  neutral: 0,
  scopes: MEMBER,
  rideOnTicket: false,
  consumer: 'SkillRules.EffectiveLevel (ADR 0066 \u00a74)',
}));
/** `ModifierRegistry.Member`: a member-scoped key whose neutral follows its op. */
const member = (
  key: string,
  op: 'add' | 'mult',
  rideOnTicket: boolean,
  consumer: string,
): ModifierKey => ({
  key,
  op,
  neutral: op === 'mult' ? 1 : 0,
  scopes: MEMBER,
  rideOnTicket,
  consumer,
});

/**
 * shared-cs `ModifierRegistry.All`, in its order (the code contract; the numbers and clamps are content). The key dropdown lists these.
 * Pinned against the C# list by validate/validate.skills.test.ts (fixtures/ot-parity.json `registry`).
 */
export const MODIFIER_REGISTRY: readonly ModifierKey[] = [
  ...LEVEL_KEYS,
  {
    key: 'base.buildTime',
    op: 'mult',
    neutral: 1,
    scopes: BASE,
    rideOnTicket: false,
    consumer: 'BuildOrderService step start (snapshot time_mult)',
  },
  {
    key: 'base.bedRate',
    op: 'mult',
    neutral: 1,
    scopes: BASE,
    rideOnTicket: false,
    consumer: 'BedRules',
  },
  {
    key: 'base.upkeepFood',
    op: 'mult',
    neutral: 1,
    scopes: BASE,
    rideOnTicket: false,
    consumer: 'UpkeepRules',
  },
  {
    key: 'base.upkeepWater',
    op: 'mult',
    neutral: 1,
    scopes: BASE,
    rideOnTicket: false,
    consumer: 'UpkeepRules',
  },
  {
    key: 'team.regen',
    op: 'mult',
    neutral: 1,
    scopes: TEAM,
    rideOnTicket: false,
    consumer: 'EnergyService',
  },
  {
    key: 'raid.energyCost',
    op: 'add',
    neutral: 0,
    scopes: TEAM,
    rideOnTicket: true,
    consumer: 'RaidModifierService',
  },
  {
    key: 'raid.hordeThreshold',
    op: 'mult',
    neutral: 1,
    scopes: TEAM,
    rideOnTicket: true,
    consumer: 'MemberKnobs.HordeThresholdMult (TicketStatsRules.FoldLeader)',
  },
  {
    key: 'raid.moveNoise',
    op: 'mult',
    neutral: 1,
    scopes: TEAM,
    rideOnTicket: true,
    consumer: 'MemberKnobs.MoveNoiseMult (TicketStatsRules.FoldLeader)',
  },
  {
    key: 'raid.searchTime',
    op: 'mult',
    neutral: 1,
    scopes: TEAM,
    rideOnTicket: true,
    consumer: 'FatigueRules',
  },
  {
    key: 'raid.lootRolls',
    op: 'add',
    neutral: 0,
    scopes: TEAM,
    rideOnTicket: true,
    consumer: 'MemberKnobs.BonusLootRolls (within skillRules.maxBonusLootRolls)',
  },
  member('combat.meleeDamage', 'mult', false, 'SurvivorCombat melee hit (D)'),
  member(
    'combat.knockback',
    'mult',
    false,
    'SurvivorCombat melee knockback, capped by skillRules.knockbackMaxM (D)',
  ),
  member(
    'combat.swingRecovery',
    'mult',
    false,
    'SurvivorCombat fire interval of melee weapons only (D)',
  ),
  member('combat.stagger', 'add', false, 'ZombieController melee stagger chance (D)'),
  member('combat.spread', 'mult', false, 'SurvivorCombat cone (D)'),
  member('combat.bloom', 'mult', false, 'BloomRules (D)'),
  member('combat.acquire', 'mult', false, 'PlayerCombatAuto acquireS (D)'),
  member(
    'combat.reload',
    'mult',
    false,
    'reload and swap seconds, floored at skillRules.reloadMinS (D)',
  ),
  member('combat.steadyTime', 'mult', false, 'SteadyAim seconds (D)'),
  member('combat.headshot', 'mult', false, 'MemberKnobs.Headshot into HeadshotInput.TraitMult (D)'),
  member(
    'combat.headshotCap',
    'add',
    false,
    'MemberKnobs.HeadshotCap: HeadshotRules cap 0.6 + this (D)',
  ),
  member(
    'combat.range',
    'mult',
    false,
    'autoCombat.targetRangeMult and the headshot falloff distance (D)',
  ),
  member('combat.aiHit', 'add', false, 'SquadmateDriver / SeatGunner hit chance (D)'),
  member('combat.bleedOut', 'mult', false, 'DownedState bleed-out (D)'),
  member('combat.revives', 'add', false, 'DownedState revives (D)'),
  member(
    'body.maxHealth',
    'add',
    true,
    'MemberKnobs.MaxHealthBonus via TicketStatsRules.MemberMaxHealth / FoldLeader: signed max HP',
  ),
  member(
    'body.sprint',
    'mult',
    true,
    'TicketStatsRules.FoldLeader into RaidModifiers.SprintMult (leader); member speed (D)',
  ),
  member(
    'body.carryKg',
    'add',
    false,
    'MemberKnobs.CarryKg via TicketStatsRules.CarryKg: capacity maths, gated off (ADR 0048)',
  ),
  member(
    'body.sprintStamina',
    'mult',
    true,
    'Raid/Stamina sprint seconds = raidRules.stamina.baseS x this (D)',
  ),
  member(
    'body.staminaRegen',
    'mult',
    true,
    'Raid/Stamina regen per second = raidRules.stamina.regenPerS x this (D)',
  ),
  member(
    'combat.steadyOnMove',
    'add',
    false,
    'PlayerCombatAuto SteadyDamp = 1 - this while moving (D)',
  ),
  member(
    'stealth.noise',
    'mult',
    true,
    "MemberKnobs.MoveNoiseMult: the leader's footstep fold into RaidModifiers (TicketStatsRules.FoldLeader)",
  ),
  member('stealth.notice', 'mult', false, 'ZombieController.Sense notice time (D)'),
  member('stealth.reach', 'add', false, 'StealthKiller reach in metres (D)'),
  member('stealth.killTime', 'mult', false, 'StealthRules.KillSeconds (D)'),
  member('field.searchTime', 'mult', false, 'SurvivorInteractor search of the member doing it (D)'),
  member('field.forceTime', 'mult', false, 'force and pick seconds (D)'),
  member('field.kickNoise', 'mult', false, 'kick and force noise (D)'),
  member('field.bandageHeal', 'mult', false, 'bandage and kit heal (D)'),
  member('field.reviveTime', 'mult', false, 'revive seconds (D)'),
  member('field.throwScatter', 'mult', false, 'ThrowableSystem landing scatter around 0.5 m (D)'),
  member('field.throwRange', 'mult', false, 'throwRangeM (D)'),
  member(
    'field.scoutDetail',
    'add',
    true,
    'MemberKnobs.ScoutDetail steps, from the signed levels (C)',
  ),
  member('arena.tank', 'mult', false, 'VehicleSim tank seconds (D)'),
  member('arena.ram', 'mult', false, 'VehicleSim ram threshold (D)'),
  member('arena.cling', 'mult', false, 'VehicleSim cling shake-off (D)'),
  member(
    'home.recruitChance',
    'add',
    false,
    'MemberKnobs.RecruitChance, added to the radio bonus in ModuleEffects.BoostChance (C)',
  ),
  member('home.tradeOffer', 'mult', false, 'MemberKnobs.TradeOffer: TradeRules offer value (C)'),
  member(
    'combat.cycle',
    'mult',
    false,
    'MemberKnobs.FireInterval: manual-action ranged weapons only (D)',
  ),
  // Card #335: a member's daily upkeep (the supply forecast and the daily upkeep both read UpkeepRules.MemberRate).
  member(
    'upkeep.foodPerDay',
    'add',
    false,
    "UpkeepRules.MemberRate: the member's daily food (forecast + daily upkeep)",
  ),
  member(
    'upkeep.waterPerDay',
    'add',
    false,
    "UpkeepRules.MemberRate: the member's daily water (forecast + daily upkeep)",
  ),
  // Wind (ADR 0073, contract §4), appended like the C# list. body.maxHealth, body.sprintStamina and body.staminaRegen above are
  // RETIRED: registered for one content version (a validator warning in a wind bundle), then removed.
  member(
    'body.windCapacity',
    'add',
    true,
    'WindRules.Capacity: the signed wind capacity (MemberWindTicket.Capacity)',
  ),
  member(
    'body.windRecovery',
    'mult',
    true,
    'WindTank.Tick recovery (signed MemberWindTicket.Recovery)',
  ),
  member('body.windRest', 'mult', false, 'WindRules.RestRatePerHour (server only)'),
  member(
    'body.windDrain',
    'add',
    true,
    'WindTank.Tick drain (signed MemberWindTicket.Drain; reserved)',
  ),
  member(
    'combat.windCost.sprint',
    'mult',
    false,
    'sprint wind cost a second (W3; client folds the signed levels and tape)',
  ),
  member('combat.windCost.melee', 'mult', false, 'melee swing wind cost (W3)'),
  member('combat.windCost.bow', 'mult', false, 'bow shot wind cost (W3)'),
  member('combat.windCost.gun', 'mult', false, 'gun recoil wind cost (W3; reserved)'),
  member('combat.pushOff', 'mult', false, 'WindTankSetup.PushOffMult: the push-off cost'),
  member(
    'combat.biteOdds',
    'mult',
    false,
    'WindTankSetup.BiteOddsMult: WindTank.PushOff bite odds',
  ),
];

export const findModifierKey = (key: string | null | undefined) =>
  MODIFIER_REGISTRY.find((k) => k.key === key) ?? null;
/** `ModifierKey.IsLevel`: a `stat.*` or `skill.*` key (capped together by effectRules.statCap, no sum clamps). */
export const isStatKey = (key: string) => key.startsWith('stat.') || key.startsWith('skill.');
